import { createFileRoute } from '@tanstack/react-router';
import { Image as ImageIcon, Plus, Search } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageDetailDialog, ImageUploader } from '@/components/features/images';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { subscribeToTableChanges } from '@/db/change-listener';
import { db } from '@/db/client';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useGroupsByImage, usePlayersByImage, useTeamsByImage } from '@/hooks/useImages';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { fadeIn, transitions } from '@/lib/motion-config';
import type { ImageId } from '@/schemas/ids';
import type { ImageEntityType, ImageRecord } from '@/schemas/image';
import { useSessionStore } from '@/stores';

export const Route = createFileRoute('/images/')({
  component: ImagesGalleryPage,
});

interface ImageGalleryCardProps {
  readonly image: ImageRecord;
  readonly onClick: () => void;
}

function ImageGalleryCard({ image, onClick }: ImageGalleryCardProps) {
  const { t } = useTranslation('common');
  const { players } = usePlayersByImage(image.id);
  const { teams } = useTeamsByImage(image.id);
  const { groups } = useGroupsByImage(image.id);

  const thumbnailUrl = image.localThumbnail
    ? URL.createObjectURL(image.localThumbnail as Blob)
    : image.remoteThumbnailUrl;

  const entityTypeMap: Record<ImageEntityType, string> = {
    player: t('images.gallery.players', 'Players'),
    session: t('images.gallery.sessions', 'Sessions'),
    game: t('images.gallery.games', 'Games'),
    team: t('images.gallery.teams', 'Teams'),
    round: t('images.gallery.rounds', 'Rounds'),
    gallery: t('images.gallery.all', 'All'),
    group: t('images.gallery.groups', 'Groups'),
  };

  const playerCount = players.length;
  const playerLabel =
    playerCount === 1
      ? t('images.gallery.onePlayer', '1 player')
      : t('images.gallery.playersCount', '{{count}} players', { count: playerCount });

  const teamCount = teams.length;
  const teamLabel =
    teamCount === 1
      ? t('images.gallery.oneTeam', '1 team')
      : t('images.gallery.teamsCount', '{{count}} teams', { count: teamCount });

  const groupCount = groups.length;
  const groupLabel =
    groupCount === 1
      ? t('images.gallery.oneGroup', '1 group')
      : t('images.gallery.groupsCount', '{{count}} groups', { count: groupCount });

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-card transition-all duration-200 hover:border-primary hover:shadow-lg hover:shadow-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={image.caption || t('images.gallery.image', 'Image')}
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted transition-colors group-hover:bg-muted/80">
          <ImageIcon className="h-8 w-8 text-muted-foreground transition-colors group-hover:text-foreground" />
        </div>
      )}
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      {/* Caption overlay */}
      {image.caption && (
        <div className="absolute bottom-0 left-0 right-0 p-3 text-left text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <p className="line-clamp-2 drop-shadow-lg">{image.caption}</p>
        </div>
      )}
      {/* Player count badge - bottom left */}
      {playerCount > 0 && (
        <div className="absolute bottom-2 left-2 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          {playerLabel}
        </div>
      )}
      {/* Team count badge - bottom left (below players if both exist) */}
      {teamCount > 0 && (
        <div
          className={`absolute ${
            playerCount > 0 ? 'bottom-10' : 'bottom-2'
          } left-2 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
        >
          {teamLabel}
        </div>
      )}
      {/* Group count badge - bottom left (below teams if both exist) */}
      {groupCount > 0 &&
        (() => {
          let bottomClass = 'bottom-2';
          if (playerCount > 0 && teamCount > 0) {
            bottomClass = 'bottom-[4.5rem]';
          } else if (playerCount > 0 || teamCount > 0) {
            bottomClass = 'bottom-10';
          }
          return (
            <div
              className={`absolute ${bottomClass} left-2 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
            >
              {groupLabel}
            </div>
          );
        })()}
      {/* Entity type badge - top right */}
      <div className="absolute top-2 right-2 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        {entityTypeMap[image.entityType] || image.entityType}
      </div>
    </button>
  );
}

function ImagesGalleryPage() {
  const { t } = useTranslation('common');
  useDocumentTitle(t('images.gallery.pageTitle', 'Image Gallery'));

  const [allImages, setAllImages] = useState<ImageRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState<ImageEntityType | 'all'>('all');
  const [selectedImageId, setSelectedImageId] = useState<ImageId | undefined>(undefined);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const loadAllImages = async () => {
    try {
      setIsLoading(true);
      const images = await db.images.filter((img) => !img.deletedAt).toArray();
      setAllImages(images);
    } catch (error) {
      console.error('Failed to load images:', error);
      setAllImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAllImages();

    const unsubscribe = subscribeToTableChanges('images', () => {
      void loadAllImages();
    });

    return unsubscribe;
  }, []);

  const filteredImages = useMemo(() => {
    let filtered = allImages;

    // Filter by entity type
    if (selectedEntityType !== 'all') {
      filtered = filtered.filter((img) => img.entityType === selectedEntityType);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (img) =>
          img.caption?.toLowerCase().includes(query) ||
          img.id.toLowerCase().includes(query) ||
          img.entityId.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [allImages, selectedEntityType, searchQuery]);

  const handleImageClick = (imageId: ImageId) => {
    setSelectedImageId(imageId);
  };

  const handleDelete = async (imageId: ImageId) => {
    const sessionStore = useSessionStore.getState();
    await sessionStore.removeImage(imageId);
    setSelectedImageId(undefined);
  };

  const handleUpdateCaption = async (imageId: ImageId, caption: string) => {
    const sessionStore = useSessionStore.getState();
    await sessionStore.updateImageCaption(imageId, caption);
  };

  const hasActiveFilters = useMemo(() => {
    return selectedEntityType !== 'all' || searchQuery.trim().length > 0;
  }, [selectedEntityType, searchQuery]);

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <motion.div
      key={routeKey}
      variants={fadeIn}
      initial={shouldAnimate ? 'initial' : false}
      animate="animate"
      transition={transitions.springGentle}
      className="w-full max-w-6xl space-y-6 px-4 mx-auto"
    >
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('images.gallery.pageTitle', 'Image Gallery')}
          </h1>
          <p className="text-muted-foreground">
            {t('images.gallery.pageDescription', 'Browse and manage all uploaded images')}
          </p>
        </div>
        {!isLoading && allImages.length > 0 && (
          <div className="w-full sm:w-auto">
            <Button onClick={() => setUploadDialogOpen(true)} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              {t('images.gallery.upload', 'Upload Image')}
            </Button>
          </div>
        )}
      </div>

      {/* Filters - visibility based on all images, not just filtered */}
      {!isLoading && allImages.length >= 3 && (
        <div className="space-y-3 rounded-lg bg-card shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full min-w-0 flex-1 sm:max-w-sm sm:min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={
                  !isLoading && allImages.length > 0
                    ? (() => {
                        if (hasActiveFilters) {
                          return t('images.gallery.searchWithCountFiltered', {
                            count: filteredImages.length,
                            filtered: filteredImages.length,
                            total: allImages.length,
                          });
                        }
                        return t('images.gallery.searchWithCount', { count: allImages.length });
                      })()
                    : t('images.gallery.searchPlaceholder', 'Search images...')
                }
                aria-label={t('images.gallery.searchPlaceholder', 'Search images...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 pl-9 min-w-0"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-shrink-0">
              <Tabs
                value={selectedEntityType}
                onValueChange={(v) => setSelectedEntityType(v as ImageEntityType | 'all')}
                className="w-full sm:w-auto sm:flex-none sm:flex-shrink-0"
                aria-label={t('images.gallery.filters.entityType.label', 'Filter by entity type')}
              >
                <TabsList className="flex h-10 w-full flex-wrap gap-1 sm:w-auto">
                  <TabsTrigger value="all">{t('images.gallery.all', 'All')}</TabsTrigger>
                  <TabsTrigger value="session">
                    {t('images.gallery.sessions', 'Sessions')}
                  </TabsTrigger>
                  <TabsTrigger value="player">{t('images.gallery.players', 'Players')}</TabsTrigger>
                  <TabsTrigger value="game">{t('images.gallery.games', 'Games')}</TabsTrigger>
                  <TabsTrigger value="team">{t('images.gallery.teams', 'Teams')}</TabsTrigger>
                  <TabsTrigger value="round">{t('images.gallery.rounds', 'Rounds')}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
      )}

      <ScrollArea className="h-[600px]">
        {(() => {
          if (isLoading) {
            return (
              <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {Array.from({ length: 20 }, (_, i) => (
                  <Skeleton key={`image-skeleton-${i}`} className="aspect-square w-full" />
                ))}
              </div>
            );
          }

          // Empty state - check allImages, not filteredImages
          if (allImages.length === 0) {
            return (
              <EmptyState
                icon={ImageIcon}
                title={t('images.gallery.empty', 'No images available')}
                description={t(
                  'images.gallery.emptyDescription',
                  'Get started by uploading your first image',
                )}
                action={{
                  label: t('images.gallery.upload', 'Upload Image'),
                  onClick: () => setUploadDialogOpen(true),
                }}
              />
            );
          }

          // No results from filters
          if (filteredImages.length === 0) {
            return (
              <EmptyState
                icon={ImageIcon}
                title={t('images.gallery.noResults', 'No images found')}
                description={t(
                  'images.gallery.noResultsDescription',
                  'Try adjusting your search or filters',
                )}
              />
            );
          }

          return (
            <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {filteredImages.map((image) => (
                <ImageGalleryCard
                  key={image.id}
                  image={image}
                  onClick={() => handleImageClick(image.id)}
                />
              ))}
            </div>
          );
        })()}
      </ScrollArea>

      <ImageDetailDialog
        open={!!selectedImageId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedImageId(undefined);
          }
        }}
        imageId={selectedImageId}
        onDelete={(id) => {
          void handleDelete(id);
        }}
        onUpdateCaption={(id, caption) => {
          void handleUpdateCaption(id, caption);
        }}
      />

      <ImageUploader
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        entityType="gallery"
        entityId="" // Will be set to imageId in addImage action
        onSuccess={() => {
          setUploadDialogOpen(false);
          void loadAllImages();
        }}
      />
    </motion.div>
  );
}
